import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { config } from '../CommonUtils/CommonURLS';
import { CommonAxiosGet, CommonAxiosPost } from '../CommonUtils/CommonAxios';
import CommonReactTable from '../CommonUtils/CommonReactTable';

const T = {
    navy: '#1a3a5c',
    font: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
};

const baseBtn = {
    width: '90px', padding: '5px 0', borderRadius: '6px', fontWeight: '600',
    fontSize: '12.5px', cursor: 'pointer', fontFamily: T.font,
};

const S = {
    mapBtn: { ...baseBtn, background: '#16a34a', color: '#fff', border: '1px solid #16a34a' },
    unmapBtn: { ...baseBtn, background: '#fff', color: '#dc2626', border: '1px solid #dc2626' },
    unmapBtnDisabled: {
        ...baseBtn, background: '#f1f5f9', color: '#94a3b8',
        border: '1px solid #cbd5e1', cursor: 'not-allowed',
    },
    badgeMapped: {
        padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
        background: '#dcfce7', color: '#166534',
    },
    badgeUnmapped: {
        padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
        background: '#f1f5f9', color: '#e90101',
    },
    emptyState: { padding: '64px 20px', textAlign: 'center' },



mappedInfo: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '5px 10px', borderRadius: '8px',
    background: '#f0fdf4', border: '1px solid #bbf7d0',
    minWidth: 0, maxWidth: '260px',
},
mappedDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    background: '#16a34a', flexShrink: 0,
},
mappedText: { display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25 },
mappedLabel: {
    fontSize: '10px', fontWeight: '600', color: '#166534',
    textTransform: 'uppercase', letterSpacing: '.4px',
},
mappedCollege: {
    fontSize: '12px', fontWeight: '600', color: '#0f172a',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
},
actionWrap: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
},
compactBtn: { width: '80px', padding: '3px 0', fontSize: '12px' },



collInfo: {
    width: '170px', textAlign: 'center', lineHeight: 1.25,
},
collLabel: {
    display: 'block', fontSize: '10px', fontWeight: '600', color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '.4px',
},
collName: {
    display: 'block', fontSize: '11px', fontWeight: '700', color: '#166534',
    whiteSpace: 'normal', overflowWrap: 'anywhere',
},
};

/* To show another field in the modal, add { label, key } to a section. */
const SECTIONS = [
    {
        title: 'Personal Details', icon: '👤',
        fields: [
            { label: 'First Name', key: 'first_name' },
            { label: 'Last Name', key: 'last_name' },
            { label: "Father's Name", key: 'father_name' },
            { label: 'Spouse Name', key: 'spouse_name' },
            { label: 'Marital Status', key: 'text' },
            { label: 'Date of Birth', key: 'dob' },
            { label: 'Caste', key: 'caste_desc' },
            { label: 'Qualification', key: 'qualification' },
            { label: 'Mobile', key: 'mobile' },
            { label: 'Email', key: 'email' },
            { label: 'Aadhaar', key: 'masked_aadhar' },
            { label: 'PAN', key: 'pan' },
        ],
    },
    {
        title: 'Service Details', icon: '💼',
        fields: [
            { label: 'CFMS Id', key: 'cfms_id' },
            { label: 'Designation', key: 'post_desc' },
            { label: 'Nature of Post', key: 'nature_of_post_name' },
            { label: 'Date of Joining', key: 'doj' },
            { label: 'Post Id', key: 'post_id' },
            { label: 'DDO Code', key: 'ddocode' },
            { label: 'University', key: 'description_long', full: true },
            { label: 'University (Short)', key: 'description_long_1', full: true },
        ],
    },
    {
        title: 'Pay Details', icon: '💰',
        fields: [
            { label: 'Pay Scale Type', key: 'payscale_type' },
            { label: 'Pay Scale Level', key: 'payscale_level' },
            { label: 'Pay Scale Range', key: 'payscale_type_1' },
            { label: 'Pay Scale Group', key: 'payscale_group' },
            { label: 'Basic Pay', key: 'basicpay', money: true },
            { label: 'Consolidated Amount', key: 'consolidated_amount', money: true },
        ],
    },
    {
        title: 'College Details', icon: '🎓',
        fields: [
            { label: 'College Name', key: 'coll_name', full: true },
            { label: 'College Code', key: 'coll_code' },
            { label: 'AISHE Code', key: 'aishe_code' },
            { label: 'District', key: 'distname' },
        ],
    },
];

const M = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
    },
    box: {
        background: '#fff', width: '100%', maxWidth: '900px', maxHeight: '90vh',
        borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 50px rgba(0,0,0,.3)', fontFamily: T.font,
    },
    header: {
        background: `linear-gradient(135deg, ${T.navy}, #2d5f8f)`, color: '#fff',
        padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px',
    },
    avatar: {
        width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255,255,255,.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '20px', fontWeight: '700', flexShrink: 0,
    },
    closeBtn: {
        marginLeft: 'auto', background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff',
        width: '32px', height: '32px', borderRadius: '50%', fontSize: '18px', cursor: 'pointer',
    },
    body: { padding: '16px 20px', overflowY: 'auto', background: '#f8fafc' },
    card: {
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px',
        marginBottom: '14px', overflow: 'hidden',
    },
    cardTitle: {
        padding: '9px 14px', background: '#eef3f9', color: T.navy,
        fontWeight: '700', fontSize: '13.5px', borderBottom: '1px solid #e2e8f0',
    },
    grid: {
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px', padding: '14px',
    },
    label: {
        fontSize: '11px', color: '#64748b', fontWeight: '600',
        textTransform: 'uppercase', letterSpacing: '.4px',
    },
    value: {
        fontSize: '13.5px', color: '#0f172a', fontWeight: '600',
        marginTop: '2px', wordBreak: 'break-word',
    },
    footer: {
        padding: '12px 20px', borderTop: '1px solid #e2e8f0',
        textAlign: 'right', background: '#fff',
    },
    viewBtn: {
        padding: '5px 12px', background: '#fff', color: T.navy, border: `1px solid ${T.navy}`,
        borderRadius: '6px', fontWeight: '600', fontSize: '12.5px', cursor: 'pointer',
        fontFamily: T.font,
    },
};

const formatValue = (f, v) => {
    if (v === null || v === undefined || String(v).trim() === '') return '-';
    if (f.money) return `₹ ${Number(v).toLocaleString('en-IN')}`;
    return String(v);
};

function EmployeeDetailsModal({ emp, onClose }) {
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!emp) return null;
    const isMapped = Number(emp.coll_mapped) === 1;

    return (
        <div style={M.overlay} onClick={onClose} role="presentation">
            <div
                style={M.box}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Employee details"
                tabIndex={-1}
            >
                <div style={M.header}>
                    <div style={M.avatar}>{(emp.emp_full_name || '?').charAt(0).toUpperCase()}</div>
                    <div>
                        <div style={{ fontSize: '17px', fontWeight: '700' }}>{emp.emp_full_name || '-'}</div>
                        <div style={{ fontSize: '12.5px', opacity: 0.85 }}>
                            {emp.post_desc || '-'} • CFMS: {emp.cfms_id || '-'}
                        </div>
                    </div>
                    <span style={{ ...(isMapped ? S.badgeMapped : S.badgeUnmapped), marginLeft: '12px' }}>
                        {isMapped ? 'Mapped' : 'Not Mapped'}
                    </span>
                    <button type="button" style={M.closeBtn} onClick={onClose} aria-label="Close">×</button>
                </div>

                <div style={M.body}>
                    {SECTIONS.map((sec) => (
                        <div key={sec.title} style={M.card}>
                            <div style={M.cardTitle}>{sec.icon} {sec.title}</div>
                            <div style={M.grid}>
                                {sec.fields.map((f) => (
                                    <div key={f.key} style={f.full ? { gridColumn: '1 / -1' } : undefined}>
                                        <div style={M.label}>{f.label}</div>
                                        <div style={M.value}>{formatValue(f, emp[f.key])}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div style={M.footer}>
                    <button type="button" style={S.unmapBtn} onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

EmployeeDetailsModal.propTypes = {
    emp: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};

/**
 * Reusable employee ↔ college mapping screen.
 *
 * Props:
 *  - title:     heading + table/excel name
 *  - listApi:   API path (after FAC_URL) that returns the employee list
 *  - empType:   'teaching' | 'nonteaching' (sent in the map payload)
 *  - excelFileName: optional, defaults to title
 */
export default function EmployeeMapping({ title, listApi, empType, excelFileName }) {
    const userId = sessionStorage.getItem('user_id');
    const [employees, setEmployees] = useState([]);
    const [viewEmp, setViewEmp] = useState(null);

    const loadEmployees = useCallback(() => {
        CommonAxiosGet(`${config.url.FAC_URL}${listApi}`).then((res) => {
            setEmployees(res?.data?.scode === '01' ? res.data.data || [] : []);
        });
    }, [listApi]);

    useEffect(() => {
        loadEmployees();
    }, [loadEmployees]);

    // Shared confirm → call API → show result → refresh flow
    const confirmAndRun = ({ swalOptions, request, successMsg, failMsg, errorMsg }) => {
        Swal.fire({
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            backdrop: false,
            ...swalOptions,
        }).then((result) => {
            if (!result.isConfirmed) return;
            request()
                .then((res) => {
                    if (res?.data?.scode === '01') {
                        Swal.fire('Success', res?.data?.sdesc || successMsg, 'success');
                        loadEmployees();
                    } else {
                        Swal.fire('Error', res?.data?.sdesc || failMsg, 'error');
                    }
                })
                .catch(() => Swal.fire('Error', errorMsg, 'error'));
        });
    };

    const handleMapEmployee = (emp) => {
        const payload = {
            coll_code: String(userId),
            empType,
            employeeCollegeMappings: [{ emp_id: String(emp.cfms_id) }],
        };
        confirmAndRun({
            swalOptions: {
                title: 'Map Employee',
                text: `Do you want to map ${emp.emp_full_name || 'this employee'} to this college?`,
                icon: 'question',
                confirmButtonText: 'Yes, Map',
                confirmButtonColor: '#16a34a',
            },
            request: () => CommonAxiosPost(`${config.url.FAC_URL}empcollegemapping`, payload),
            successMsg: 'Employee mapped successfully.',
            failMsg: 'Employee mapping failed.',
            errorMsg: 'Unable to map employee.',
        });
    };

    const handleUnmapEmployee = (emp) => {
        if (String(emp?.coll_code) !== String(userId)) {
            Swal.fire({
                title: 'Not Allowed',
                text: 'You can only unmap employees mapped to your own college.',
                icon: 'warning',
                confirmButtonColor: T.navy,
                backdrop: false,
            });
            return;
        }
        confirmAndRun({
            swalOptions: {
                title: 'Unmap Employee',
                text: `Do you want to remove ${emp.emp_full_name || 'this employee'} from this college?`,
                icon: 'warning',
                confirmButtonText: 'Yes, Unmap',
                confirmButtonColor: '#dc2626',
            },
            request: () => CommonAxiosPost(`${config.url.FAC_URL}empcollegemappingdelete/${emp.sno}`, {}),
            successMsg: 'Employee unmapped successfully.',
            failMsg: 'Employee unmapping failed.',
            errorMsg: 'Unable to unmap employee.',
        });
    };

    const centered = { style: { textAlign: 'center' } };

    const columns = [
        { id: 'slNo', header: 'Sl.No', cell: ({ row }) => row.index + 1 },
        { id: 'cfms_id', header: 'CFMS Id', accessorKey: 'cfms_id' },
        { id: 'emp_full_name', header: 'Employee Name', accessorKey: 'emp_full_name' },
        { id: 'mobile', header: 'Mobile No', accessorKey: 'mobile' },
        { id: 'masked_aadhar', header: 'Aadhaar No', accessorKey: 'masked_aadhar' },
        { id: 'post_desc', header: 'Designation', accessorKey: 'post_desc' },
        { id: 'nature_of_post_name', header: 'Post', accessorKey: 'nature_of_post_name' },
        {
            id: 'view',
            header: 'View',
            meta: centered,
            cell: ({ row }) => (
                <button type="button" style={M.viewBtn} onClick={() => setViewEmp(row.original)}>
                    👁 View
                </button>
            ),
        },
        // {
        //     id: 'action',
        //     header: 'Action',
        //     meta: centered,
        //     cell: ({ row }) => {
        //         const emp = row.original;
        //         const isMapped = Number(emp.coll_mapped) === 1;
        //         const isOwnCollege = String(emp.coll_code) === String(userId);

        //         if (!isMapped) {
        //             return (
        //                 <div style={S.actionWrap}>
        //                     <button type="button" style={S.mapBtn} onClick={() => handleMapEmployee(emp)}>
        //                         Map
        //                     </button>
        //                 </div>
        //             );
        //         }
        //         return (
        //             <div style={S.actionWrap}>
        //                 <button
        //                     type="button"
        //                     style={isOwnCollege ? S.unmapBtn : S.unmapBtnDisabled}
        //                     disabled={!isOwnCollege}
        //                     onClick={() => handleUnmapEmployee(emp)}
        //                 >
        //                     Unmap
        //                 </button>
        //                 <span style={S.collName} title={emp.coll_name}>
        //                     Mapped to {emp.coll_name || '-'}
        //                 </span>
        //             </div>
        //         );
        //     },
        // },

//         {
//     id: 'action',
//     header: 'Action',
//     meta: { style: { textAlign: 'center' } },
//     cell: ({ row }) => {
//         const emp = row.original;
//         const isMapped = Number(emp.coll_mapped) === 1;
//         const isOwnCollege = String(emp.coll_code) === String(userId);

//         if (!isMapped) {
//             return (
//                 <div style={{ ...S.actionWrap, justifyContent: 'center' }}>
//                     <button type="button" style={S.mapBtn} onClick={() => handleMapEmployee(emp)}>
//                         Map
//                     </button>
//                 </div>
//             );
//         }

//         return (
//             <div style={S.actionWrap}>
//                 <button
//                     type="button"
//                     style={isOwnCollege ? S.unmapBtn : S.unmapBtnDisabled}
//                     disabled={!isOwnCollege}
//                     title={isOwnCollege ? 'Remove from your college' : 'Mapped to another college'}
//                     onClick={() => handleUnmapEmployee(emp)}
//                 >
//                     Unmap
//                 </button>

//                 <div style={S.mappedInfo} title={emp.coll_name}>
//                     <span style={S.mappedDot} />
//                     <div style={S.mappedText}>
//                         <span style={S.mappedLabel}>Mapped to</span>
//                         <span style={S.mappedCollege}>{emp.coll_name || '-'}</span>
//                     </div>
//                 </div>
//             </div>
//         );
//     },
// },
{
    id: 'action',
    header: 'Action',
    meta: { style: { textAlign: 'center' } },
    cell: ({ row }) => {
        const emp = row.original;
        const isMapped = Number(emp.coll_mapped) === 1;
        const isOwnCollege = String(emp.coll_code) === String(userId);

        if (!isMapped) {
            return (
                <div style={S.actionWrap}>
                    <button
                        type="button"
                        style={{ ...S.mapBtn, ...S.compactBtn }}
                        onClick={() => handleMapEmployee(emp)}
                    >
                        Map
                    </button>
                </div>
            );
        }

        return (
            <div style={S.actionWrap}>
                <button
                    type="button"
                    style={{ ...(isOwnCollege ? S.unmapBtn : S.unmapBtnDisabled), ...S.compactBtn }}
                    disabled={!isOwnCollege}
                    title={isOwnCollege ? 'Remove from your college' : 'Mapped to another college'}
                    onClick={() => handleUnmapEmployee(emp)}
                >
                    Unmap
                </button>
                <div style={S.collInfo}>
    <span style={S.collLabel}>Mapped to</span>
    <span style={S.collName}>{emp.coll_name || '-'}</span>
</div>
            </div>
        );
    },
},
    ];

    return (
        <div className="faculty-card">
            <div className="fac-topbar">
                <div className="fac-detnew">
                    <h5 className="mb-0">{title}</h5>
                </div>
            </div>

            {employees.length > 0 ? (
                <CommonReactTable
                    data={employees}
                    columns={columns}
                    headerName={title}
                    showFooter={false}
                    showPagination={false}
                    showExcel={true}
                    showPrint={true}
                    excelFileName={excelFileName || title}
                />
            ) : (
                <div style={S.emptyState}>
                    <div style={{ fontSize: '42px', marginBottom: '12px', opacity: 0.3 }}>🎓</div>
                    <p style={{ fontWeight: '700', color: '#c0392b', margin: 0, fontSize: '15px' }}>No Data Found</p>
                    <p style={{ color: '#bbb', fontSize: '13px', marginTop: '6px' }}>
                        No employees are available for mapping.
                    </p>
                </div>
            )}

            {viewEmp && <EmployeeDetailsModal emp={viewEmp} onClose={() => setViewEmp(null)} />}
        </div>
    );
}

EmployeeMapping.propTypes = {
    title: PropTypes.string.isRequired,
    listApi: PropTypes.string.isRequired,
    empType: PropTypes.oneOf(['teaching', 'nonteaching']).isRequired,
    excelFileName: PropTypes.string,
};